import React, { useEffect, useState } from "react";
import { Button, TextField, Autocomplete, Avatar, Chip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DatePickerWithTimeComponent from "../DatePickerWithTimeComponent";
import { addDoc, collection, serverTimestamp, query, where, limit, onSnapshot, updateDoc, doc } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import { showCustomToast } from "../Misc/CustomToast";
import SearchIcon from "@mui/icons-material/Search";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import MovieCreationIcon from "@mui/icons-material/MovieCreation";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import UploaderDocForNote from "../Updloader/UploaderDocForNote";

function AddCBTNoteWithClients({ closemodal }) {
  const { currentUser } = useAuth();

  const [sessionDate, setSessionDate] = useState(new Date());
  const [sessionNumber, setSessionNumber] = useState("");
  const [presentation, setPresentation] = useState("");
  const [treatmentGoal, setTreatmentGoal] = useState("");
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const draftDelay = 800;
  const draftKey = `draft:cbtNoteWithClient:${currentUser?.uid || "anon"}`;

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedClientName, setSelectedClientName] = useState("");
  const [unsubscribe, setUnsubscribe] = useState(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Restore draft on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.sessionDate) {
          const d = new Date(parsed.sessionDate);
          if (!isNaN(d)) setSessionDate(d);
        }
        if (parsed?.sessionNumber) setSessionNumber(parsed.sessionNumber);
        if (parsed?.presentation) setPresentation(parsed.presentation);
        if (parsed?.treatmentGoal) setTreatmentGoal(parsed.treatmentGoal);
        if (parsed?.selectedClient) {
          setSelectedClient(parsed.selectedClient);
          setSelectedClientName(parsed.selectedClientName || "");
          setSearchQuery(parsed.selectedClientName || "");
        }
      }
    } catch (e) {
      console.warn("CBT with clients draft load error:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autosave draft with debounce
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(
          draftKey,
          JSON.stringify({
            sessionDate: sessionDate?.toISOString?.() || null,
            sessionNumber,
            presentation,
            treatmentGoal,
            selectedClient,
            selectedClientName,
          })
        );
        setIsSavingDraft(false);
      } catch (e) {
        console.warn("CBT with clients draft save error:", e);
      }
    }, draftDelay);
    setIsSavingDraft(true);
    return () => clearTimeout(t);
  }, [
    sessionDate,
    sessionNumber,
    presentation,
    treatmentGoal,
    selectedClient,
    selectedClientName,
    draftKey,
  ]);

  useEffect(() => {
    let newUnsubscribe;
    if (debouncedSearchQuery.length > 0) {
      setLoading(true);
      const search = query(
        collection(db, "clients"),
        where(
          "searchTerms",
          "array-contains-any",
          debouncedSearchQuery.trim().toLowerCase().split(" ")
        ),
        limit(10)
      );
      newUnsubscribe = onSnapshot(search, (snapshot) => {
        const results = [];
        snapshot.forEach((doc) => {
          results.push({
            id: doc.id,
            ...doc.data(),
          });
        });
        setClients(results);
        setLoading(false);
      });
    } else {
      setClients([]);
    }
    setUnsubscribe(() => newUnsubscribe);
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [debouncedSearchQuery]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setSelectedClient(null);
    setSelectedClientName("");
  };

  const handleSelect = (event, value) => {
    if (value) {
      const client = clients.find(
        (client) => `${client.firstName} ${client.lastName}` === value
      );
      if (client) {
        setSelectedClient(client);
        setSelectedClientName(`${client.firstName} ${client.lastName}`);
      } else {
        setSelectedClient(null);
        setSelectedClientName("");
      }
    } else {
      setSelectedClient(null);
      setSelectedClientName("");
    }
  };

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const filtered = selectedFiles.filter((f) => f.size <= 50 * 1024 * 1024);
    const rejected = selectedFiles.filter((f) => f.size > 50 * 1024 * 1024);
    if (rejected.length) {
      showCustomToast("Some files exceed 50MB and were skipped.", "error");
    }
    setFiles((prev) => [...prev, ...filtered]);
  };

  const uploadFilesAndGetUrls = async (docId, clientId) => {
    const storage = getStorage();
    const fileUrls = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const prefix = clientId ? `clients/${clientId}` : "cbtNotes";
      const filePath = `Documents/${prefix}/${docId}/${file.name}`;
      const storageRef = ref(storage, filePath);
      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);
      fileUrls.push({ url: fileUrl, name: file.name });
    }
    return fileUrls;
  };

  const handleAddNote = async () => {
    if (!selectedClient) {
      showCustomToast("Please select a client.", "error");
      return;
    }
    if (!sessionDate || !sessionNumber || !presentation || !treatmentGoal) {
      showCustomToast("Please fill all mandatory fields.", "error");
      return;
    }
    setIsLoading(true);
    try {
      const baseData = {
        noteType: "cbtNote",
        sessionDateTime: sessionDate,
        sessionNumber,
        presentation,
        treatmentGoal,
        createdAt: serverTimestamp(),
        effectiveDateTime: sessionDate,
        doneBy: currentUser?.displayName || currentUser?.email || "Unknown",
        clientId: selectedClient.id,
        clientFirstName: selectedClient.firstName,
        clientLastName: selectedClient.lastName,
        clientProfilePicture: selectedClient.profilePicture || "",
      };
      const docRef = await addDoc(collection(db, "notes"), baseData);
      const fileUrls = await uploadFilesAndGetUrls(docRef.id, selectedClient.id);
      if (fileUrls.length) {
        await updateDoc(doc(db, "notes", docRef.id), { fileUrls });
      }
      showCustomToast("CBT note added successfully.", "success");
      setIsLoading(false);
      closemodal();
    } catch (e) {
      console.error(e);
      showCustomToast("Error adding CBT note.", "error");
      setIsLoading(false);
    }
  };

  const getFileIcon = (name = "") => {
    const ext = name.split(".").pop()?.toLowerCase();
    if (["pdf"].includes(ext)) return <PictureAsPdfIcon sx={{ color: "#d32f2f" }} />;
    if (["png", "jpg", "jpeg", "gif", "bmp", "webp"].includes(ext))
      return <ImageIcon sx={{ color: "#1976d2" }} />;
    if (["mp3", "wav", "aac", "ogg", "m4a"].includes(ext))
      return <AudiotrackIcon sx={{ color: "#8e24aa" }} />;
    if (["mp4", "mov", "avi", "mkv", "webm"].includes(ext))
      return <MovieCreationIcon sx={{ color: "#6d4c41" }} />;
    if (["zip", "rar", "7z"].includes(ext)) return <FolderZipIcon sx={{ color: "#f57c00" }} />;
    if (["doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "csv"].includes(ext))
      return <DescriptionIcon sx={{ color: "#2e7d32" }} />;
    return <InsertDriveFileIcon sx={{ color: "#607d8b" }} />;
  };

    return (
      <div style={{ width: "auto", height: "100%" }}>
        <div className="line1-modal-percent">
          <div className="modal-title">Add CBT Note</div>
          <CloseIcon
          style={{
            marginRight: "16px",
            color: "rgb(214, 213, 213)",
            cursor: "pointer",
          }}
          onClick={closemodal}
        />
      </div>

      <div
        style={{
          width: "100%",
          marginTop: "10px",
          height: "75%",
          overflowY: "scroll",
          overflowX: "hidden",
          padding: "12px 16px 0",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              width: "48%",
              paddingTop: "10px",
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
            }}
          >
            <div
              style={{
                width: "100%",
                marginBottom: 8,
              }}
            >
              <Autocomplete
                freeSolo
                disableClearable
                options={clients.map(
                  (client) => `${client.firstName} ${client.lastName}`
                )}
                renderOption={(props, option) => {
                  const client = clients.find(
                    (client) =>
                      `${client.firstName} ${client.lastName}` === option
                  );
                  return (
                    <li
                      {...props}
                      key={client.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        borderBottom: "1px solid #BEBCBC",
                      }}
                    >
                      {client.profilePicture && (
                        <Avatar
                          src={client.profilePicture}
                          style={{
                            marginRight: "10px",
                            width: "40px",
                            height: "40px",
                          }}
                        />
                      )}
                      <div style={{ width: "220px" }}>
                        {`${client.firstName} ${client.lastName}`}
                      </div>
                      {client.clientIdentifier}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <div
                    ref={params.InputProps.ref}
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="text"
                      {...params.inputProps}
                      onChange={handleSearchChange}
                      value={searchQuery}
                      placeholder="Search client..."
                      style={{
                        width: "100%",
                        height: "45px",
                        borderRadius: "5px",
                        paddingLeft: "10px",
                        background: "#FFFFFF",
                        border: "1px solid #BEBCBC",
                      }}
                    />
                    <SearchIcon />
                  </div>
                )}
                onChange={handleSelect}
              />
            </div>

            <div
              style={{
                marginTop: "4px",
                fontWeight: "bold",
                display: "flex",
                justifyContent: "flex-start",
                width: "100%",
              }}
            >
              {selectedClientName ? (
                <>Selected Client: {selectedClientName}</>
              ) : loading ? (
                <>Searching...</>
              ) : (
                <>Please select a client</>
              )}
            </div>
          </div>
          <div
            style={{
              width: "48%",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <DatePickerWithTimeComponent
              title="Session date & time"
              value={sessionDate}
              onChange={setSessionDate}
              leftMargin="none"
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            background: "#f8f9fb",
            borderRadius: 12,
            padding: "16px 18px",
            border: "1px solid #e5e7eb",
            marginTop: 16,
          }}
        >
          <div>
            <div className="label-textfield" style={{ marginBottom: 6 }}>
              Session number
            </div>
            <TextField
              value={sessionNumber}
              onChange={(e) => setSessionNumber(e.target.value)}
              size="small"
              fullWidth
              style={{
                borderRadius: "5px",
                backgroundColor: "#f2eeef",
                maxWidth: 260,
              }}
              placeholder="e.g. 3"
            />
          </div>
          <div>
            <div className="label-textfield" style={{ marginBottom: 6 }}>
              Presentation
            </div>
            <TextField
              value={presentation}
              onChange={(e) => setPresentation(e.target.value)}
              style={{
                width: "100%",
                borderRadius: "5px",
                backgroundColor: "#f2eeef",
              }}
              placeholder="Describe presentation"
              multiline
              rows={4}
            />
          </div>
          <div>
            <div className="label-textfield" style={{ marginBottom: 6 }}>
              Treatment goal worked on
            </div>
            <TextField
              value={treatmentGoal}
              onChange={(e) => setTreatmentGoal(e.target.value)}
              style={{
                width: "100%",
                borderRadius: "5px",
                backgroundColor: "#f2eeef",
              }}
              placeholder="Describe treatment goal"
              multiline
              rows={4}
            />
          </div>
          <div
            style={{
              background: "#fff",
              border: "1px dashed #cbd5e1",
              borderRadius: 10,
              padding: 12,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Attachments</div>
            {files?.length ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                {files.map((f, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                      background: "#f8fafc",
                    }}
                  >
                    {getFileIcon(f.name)}
                    <div style={{ fontSize: 13, color: "#111" }}>{f.name}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                No files selected
              </div>
            )}
            <UploaderDocForNote handleFileChange={handleFileChange} files={files} />
            <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
              Max file size: 50 MB each.
            </div>
          </div>
        </div>
      </div>
      <div
        className="line4-modal-percent"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 10,
        }}
      >
        <Chip
          label={
            isSavingDraft
              ? "Saving draft..."
              : sessionNumber || presentation || treatmentGoal || selectedClientName
              ? "Draft saved"
              : "No draft"
          }
          size="small"
          sx={{
            backgroundColor: isSavingDraft
              ? "#f9a825"
              : sessionNumber || presentation || treatmentGoal || selectedClientName
              ? "#2e7d32"
              : "#607d8b",
            color: "#fff",
            fontWeight: 700,
          }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            try {
              localStorage.removeItem(draftKey);
            } catch {}
            setSessionDate(new Date());
            setSessionNumber("");
            setPresentation("");
            setTreatmentGoal("");
            setFiles([]);
            setSelectedClient(null);
            setSelectedClientName("");
            setSearchQuery("");
          }}
        >
          Clear draft
        </Button>
        <Button
          variant="contained"
          style={{ backgroundColor: "white", color: "black" }}
          onClick={closemodal}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleAddNote}
          disabled={isLoading}
          style={
            isLoading
              ? { backgroundColor: "red", color: "white" }
              : { backgroundColor: "#1565C0", color: "white" }
          }
        >
          {isLoading ? "Saving..." : "Add CBT Note"}
        </Button>
      </div>
    </div>
  );
}

export default AddCBTNoteWithClients;
